import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './SafariBookingForm.css';

const SafariBookingForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    numberOfPeople: 1,
    numberOfChildren: 0,
    childrenAges: [], // Add this new field
    date: new Date(),
    safariTime: 'morning',
    safariType: '',
    idType: '',
    idNumber: '',
    nationality: 'indian',
    specialRequirements: ''
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertVariant, setAlertVariant] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    const newChildrenAges = Array(formData.numberOfChildren).fill('');
    setFormData(prev => ({
      ...prev,
      childrenAges: newChildrenAges
    }));
  }, [formData.numberOfChildren]);

  const handleChildAgeChange = (index, age) => {
    const newChildrenAges = [...formData.childrenAges];
    newChildrenAges[index] = age;
    setFormData({
      ...formData,
      childrenAges: newChildrenAges
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/book-safari`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          date: formData.date.toISOString(),
          safariTime: formData.safariTime,
          safariType: formData.safariType,
          numberOfChildren: formData.numberOfChildren,
          childrenAges: formData.childrenAges
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setAlertVariant('success');
        setAlertMessage('Safari booking request sent successfully! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          numberOfPeople: 1,
          date: new Date(),
          safariTime: 'morning',
          safariType: '',
          idType: '',
          idNumber: '',
          nationality: 'indian',
          specialRequirements: ''
        });
      } else {
        throw new Error(data.message || 'Something went wrong');
      }
    } catch (error) {
      setAlertVariant('danger');
      setAlertMessage('Failed to send booking request. Please try again.');
    }
    setShowAlert(true);
  };

  return (
    <div className="safari-booking-form">
      {showAlert && (
        <Alert 
          variant={alertVariant} 
          onClose={() => setShowAlert(false)} 
          dismissible
        >
          {alertMessage}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Full Name</Form.Label>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Phone</Form.Label>
          <Form.Control
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Nationality</Form.Label>
          <Form.Select
            value={formData.nationality}
            onChange={(e) => setFormData({...formData, nationality: e.target.value})}
            required
          >
            <option value="indian">Indian</option>
            <option value="foreign">Foreign National</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>ID Type</Form.Label>
          <Form.Select
            value={formData.idType}
            onChange={(e) => setFormData({...formData, idType: e.target.value})}
            required
          >
            <option value="">Select ID Type</option>
            <option value="aadhar">Aadhar Card</option>
            <option value="pan">PAN Card</option>
            <option value="driving">Driving License</option>
            <option value="passport">Passport</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>ID Number</Form.Label>
          <Form.Control
            type="text"
            value={formData.idNumber}
            onChange={(e) => setFormData({...formData, idNumber: e.target.value})}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Safari Type</Form.Label>
          <Form.Select
            value={formData.safariType}
            onChange={(e) => setFormData({...formData, safariType: e.target.value})}
            required
          >
            <option value="">Select Safari Type</option>
            <option value="jeep">Jeep Safari (Max 6 persons)</option>
            <option value="canter">Canter Safari (16-seater bus)</option>
          </Form.Select>
          {formData.safariType && (
            <Form.Text className="text-muted">
              {formData.safariType === 'jeep' 
                ? 'Maximum 6 individuals allowed, including 2 children under 12 years'
                : 'Canter is a shared 16-seater safari bus.'}
            </Form.Text>
          )}
        </Form.Group>

        <div className="row">
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>Number of Adults</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max={formData.safariType === 'jeep' ? 6 : 4}
                value={formData.numberOfPeople}
                onChange={(e) => setFormData({...formData, numberOfPeople: parseInt(e.target.value)})}
                required
              />
              <Form.Text className="text-muted">
                {formData.safariType === 'jeep' 
                  ? 'Maximum 6 people per jeep including children' 
                  : 'Maximum 4 people for canter booking'}
              </Form.Text>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group className="mb-3">
              <Form.Label>Number of Children</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max={formData.safariType === 'jeep' ? 2 : 4}
                value={formData.numberOfChildren}
                onChange={(e) => {
                  const childrenCount = parseInt(e.target.value) || 0;
                  setFormData({...formData, numberOfChildren: childrenCount});
                }}
              />
              <Form.Text className="text-muted">
                {formData.safariType === 'jeep' && 'Maximum 2 children allowed per jeep'}
              </Form.Text>
            </Form.Group>
          </div>
        </div>

        {formData.numberOfChildren > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <Form.Label>Children's Ages</Form.Label>
            </div>
            {[...Array(formData.numberOfChildren)].map((_, index) => (
              <div className="col-md-3 col-6 mb-2" key={index}>
                <Form.Control
                  type="number"
                  placeholder={`Child ${index + 1} age`}
                  value={formData.childrenAges[index]}
                  onChange={(e) => handleChildAgeChange(index, e.target.value)}
                  min="0"
                  max="17"
                  required
                />
              </div>
            ))}
            <div className="col-12">
              <Form.Text className="text-muted">
                Please enter age for each child
              </Form.Text>
            </div>
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Preferred Date</Form.Label>
          <DatePicker
            selected={formData.date}
            onChange={(date) => setFormData({...formData, date})}
            minDate={new Date()}
            className="form-control"
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Safari Time</Form.Label>
          <Form.Select
            value={formData.safariTime}
            onChange={(e) => setFormData({...formData, safariTime: e.target.value})}
            required
          >
            <option value="morning">Morning (6:00 AM - 9:00 AM)</option>
            <option value="evening">Evening (2:30 PM - 6:00 PM)</option>
          </Form.Select>
          <Form.Text className="text-muted">
            Safari timing varies as season changes
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Special Requirements</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={formData.specialRequirements}
            onChange={(e) => setFormData({...formData, specialRequirements: e.target.value})}
          />
        </Form.Group>

        <Button 
          type="submit" 
          style={{
            backgroundColor: '#D4AF37',
            border: 'none',
            width: '100%',
            padding: '12px'
          }}
        >
          Submit Safari Booking Request
        </Button>
      </Form>
    </div>
  );
};

export default SafariBookingForm;
